import {
    CopilotChatUserMessage as CpkUserMessage,
    CopilotChatAssistantMessage as CpkAssistantMessage,
    CopilotChatReasoningMessage as CpkReasoningMessage,
} from '@copilotkit/react-core/v2'
import type {
    CopilotChatUserMessageProps,
    CopilotChatAssistantMessageProps,
} from '@copilotkit/react-core/v2'
import { Group } from '@mantine/core'
import { IconUser, IconRobot } from '@tabler/icons-react'
import { genericMemo } from '../../../components/genericMemo'
import type { ComponentProps, FC } from 'react'

export const UserMessage: FC<CopilotChatUserMessageProps> = genericMemo(
    (props) => (
        <Group wrap='nowrap' align='flex-start' justify='flex-end'>
            {/* Give the message its full share of the row. The v2 bubble is
                capped at max-w-[80%] of THIS element; without a flex-grow
                wrapper the bubble shrink-wraps to its content, so the 80%
                resolves against the bubble's own width and forces short text
                to wrap. Mirrors the assistant message's flex:1 wrapper. */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <CpkUserMessage {...props} />
            </div>
            {/* v2 user bubble has pt-10 (2.5rem) of top padding before the
                text; match it so the icon aligns with the first line of
                content rather than the bubble's outer edge. */}
            <IconUser style={{ flexShrink: 0, marginTop: '2.5rem' }} />
        </Group>
    )
)

export const AssistantMessage: FC<CopilotChatAssistantMessageProps> =
    genericMemo((props) => {
        const content = (props.message as { content?: unknown } | undefined)
            ?.content
        const hasContent = !!content
        return (
            <Group wrap='nowrap' align='flex-start'>
                {hasContent ? (
                    <IconRobot
                        style={{
                            flexShrink: 0,
                            // v2's assistant content uses `cpk:prose`, whose
                            // first <p> carries margin-top: 1.25rem. Offset the
                            // icon by the same amount so it aligns with the
                            // first line of rendered markdown instead of the
                            // bubble's outer edge.
                            marginTop: '1.25rem',
                            color: 'var(--mantine-color-anchor)',
                        }}
                    />
                ) : (
                    // Content-less assistant messages (tool / HITL widget
                    // renders like ChoicePrompt) have no avatar. Reserve the
                    // same 24px icon gutter (IconRobot's default size) so the
                    // rendered widget lines up with the text column instead of
                    // sitting flush against the avatar edge.
                    <div style={{ width: 24, flexShrink: 0 }} aria-hidden />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <CpkAssistantMessage {...props} />
                </div>
            </Group>
        )
    })

// Reasoning header: keep CopilotKit's status chrome — the "Thinking…" /
// "Thought for X seconds" label plus a pulse while the model reasons — but
// drop the chevron. Paired with a `contentView` slot that renders nothing
// (see ThreadPage's messageView), the agent's raw chain-of-thought tokens
// are never shown, so the expand/collapse affordance the chevron drives
// would have nothing to reveal. The built-in header only pulses *before*
// tokens arrive (handing the live animation off to the content area we've
// hidden), so we key the pulse off `isStreaming` alone to keep the "still
// thinking" cue visible for the whole run.
export const ReasoningHeader: FC<
    ComponentProps<typeof CpkReasoningMessage.Header>
> = genericMemo(({ label, isStreaming }) => (
    <div className='cpk:inline-flex cpk:items-center cpk:gap-1 cpk:py-1 cpk:text-sm cpk:text-muted-foreground cpk:select-none'>
        <span className='cpk:font-medium'>{label}</span>
        {isStreaming && (
            <span className='cpk:inline-flex cpk:items-center cpk:ml-1'>
                <span className='cpk:w-1.5 cpk:h-1.5 cpk:rounded-full cpk:bg-muted-foreground cpk:animate-pulse' />
            </span>
        )}
    </div>
))
